import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  orangeButton,
  primaryBtnTeal,
  selectStyles,
  textfieldTextPrep,
} from "../../../../style/PatientDetailsStyle";
import {
  ExtractMedicineSummaryV1,
  MedicineUploadScan,
  generateMedicineSalt,
  postBulkMedicine,
} from "../../../../connections/postApi";
import { getMedicineSaltPharmacySearch } from "../../../../connections/getApi";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { uploadFileToS3 } from "../../../../connections/s3Upload";
import { autoFocusOffWithOutBorder } from "../../../../style/PatinetFormStyle";
import UseGetAllMedicineSaltList from "../../../../helper/UseGetAllMedicineSaltList";
import UseFetchFormType from "../../../../helper/UseFetchFormType";
import CreatableSelect from "react-select/creatable";
import AddIcon from "@mui/icons-material/Add";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import { xBtn } from "../../../../style/LapPageStyle";
import { textfieldText } from "../../../../style/AppointmentStyle";
import { showToast } from "../../../../utils/toastUtil";
import {
  normalizeMedicineSalt,
  validateMedicineSalt,
} from "../../../../utils/medicineSaltUtils";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";

const MedicineImageUpload = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [medicines, setMedicines] = useState([]);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [saltLoadingIndex, setSaltLoadingIndex] = useState(null);
  const [extraSaltOptions, setExtraSaltOptions] = useState([]);

  // const { data: medicineList } = UseGetAllMedicineList();
  const { data: medicineSaltList, isLoading: medicineSaltLoading } =
    UseGetAllMedicineSaltList();
  const { data: formType } = UseFetchFormType();

  // const medicineOptions = useMemo(() => {
  //     return (
  //         medicineList?.payload?.medicines?.map(([id, name]) => ({
  //             value: id,
  //             label: name,
  //         })) || []
  //     );
  // }, [medicineList]);

  const medicineSaltOptions = useMemo(() => {
    return (
      medicineSaltList?.payload?.medicine_salt?.map(([id, name]) => ({
        value: id,
        label: name,
      })) || []
    );
  }, [medicineSaltList]);

  const handleUpload = async () => {
    if (!file) return;
    try {
      setLoading(true);
      const { key } = await uploadFileToS3(file, "GlobalAiContext", 0);
      const res = await ExtractMedicineSummaryV1(key, "medicine");
      if (res?.payload && res?.payload?.summaries?.medicines?.length > 0) {
        setStep(2);
        const mappedMedicines = res?.payload?.summaries?.medicines.map(
          (item) => {
            const saltName = item?.salt?.toUpperCase() || "";
            // const matchedSalt = medicineSaltOptions.find(
            //     (opt) => opt.label.toUpperCase() === saltName
            // );
            return {
              medicine_name: item?.medicine_name?.toUpperCase() || "",
              medicine_salt: normalizeMedicineSalt(saltName),
              form_type: item?.form_type || "",
              isNewSalt: true,
            };
          },
        );
        setMedicines(mappedMedicines);
      } else {
        showToast("No Medicine found ", "ERROR");
      }
      // onSuccess(res.data.medicines.medicines);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRow = () => {
    setMedicines((prev) => [
      ...prev,
      {
        medicine_name: "",
        medicine_salt: "",
        form_type: "",
        isNewSalt: false,
      },
    ]);
  };

  const handleAutoFillSalt = async (index) => {
    const medicineName = medicines[index]?.medicine_name?.trim();
    if (!medicineName) {
      showToast("Enter a medicine name first", "ERROR");
      return;
    }
    setSaltLoadingIndex(index);
    try {
      const res = await ExtractMedicineSummaryV1("", "medicine_salt", "GlobalAiContext", null, null, [medicineName]);
      const salt = res?.payload?.summaries?.medicines?.[0]?.salt;
      if (!salt) { showToast("Could not determine salt. Please enter manually.", "ERROR"); return; }
      const normalized = normalizeMedicineSalt(salt);

      const searchRes = await getMedicineSaltPharmacySearch(normalized, 1, 5);
      const existing = searchRes?.payload?.medicine_salts || [];
      const match = existing.find((s) => s.name?.toLowerCase().trim() === normalized.toLowerCase().trim());

      if (match) {
        setExtraSaltOptions((prev) =>
          prev.some((o) => o.value === match.id) ? prev : [...prev, { value: match.id, label: match.name }]
        );
        setMedicines((prev) =>
          prev.map((med, i) => i === index ? { ...med, medicine_salt: match.name, isNewSalt: false } : med)
        );
        setErrors((prev) => { const copy = { ...prev }; if (copy[index]) delete copy[index].medicine_salt; return copy; });
        showToast(`Salt "${match.name}" found & selected`, "SUCCESS");
      } else {
        const result = await generateMedicineSalt({ medicine_salt: { name: normalized }, medicines_ids: [] });
        if (result?.status?.status === "SUCCESS") {
          const newSalt = result?.payload?.medicine_salt || result?.payload;
          const newId = newSalt?.id || null;
          const newName = newSalt?.name || normalized;
          setExtraSaltOptions((prev) =>
            prev.some((o) => o.value === newId) ? prev : [...prev, { value: newId || newName, label: newName }]
          );
          setMedicines((prev) =>
            prev.map((med, i) => i === index ? { ...med, medicine_salt: newName, isNewSalt: false } : med)
          );
          setErrors((prev) => { const copy = { ...prev }; if (copy[index]) delete copy[index].medicine_salt; return copy; });
          showToast(result?.status?.message || "Salt created & selected", "SUCCESS");
        } else {
          showToast(result?.status?.message || "Failed to create salt", "ERROR");
        }
      }
    } catch {
      showToast("Failed to auto-fill salt", "ERROR");
    } finally {
      setSaltLoadingIndex(null);
    }
  };

  const handleCreateSaltAtIndex = async (inputValue, index) => {
    const normalized = normalizeMedicineSalt(inputValue);
    setSaltLoadingIndex(index);
    try {
      const searchRes = await getMedicineSaltPharmacySearch(normalized, 1, 5);
      const existing = searchRes?.payload?.medicine_salts || [];
      const match = existing.find((s) => s.name?.toLowerCase().trim() === normalized.toLowerCase().trim());

      if (match) {
        setExtraSaltOptions((prev) =>
          prev.some((o) => o.value === match.id) ? prev : [...prev, { value: match.id, label: match.name }]
        );
        setMedicines((prev) =>
          prev.map((med, i) => i === index ? { ...med, medicine_salt: match.name, isNewSalt: false } : med)
        );
        setErrors((prev) => { const copy = { ...prev }; if (copy[index]) delete copy[index].medicine_salt; return copy; });
        showToast(`Salt "${match.name}" already exists & selected`, "SUCCESS");
      } else {
        const result = await generateMedicineSalt({ medicine_salt: { name: normalized }, medicines_ids: [] });
        if (result?.status?.status === "SUCCESS") {
          const newSalt = result?.payload?.medicine_salt || result?.payload;
          const newId = newSalt?.id || null;
          const newName = newSalt?.name || normalized;
          setExtraSaltOptions((prev) =>
            prev.some((o) => o.value === newId) ? prev : [...prev, { value: newId || newName, label: newName }]
          );
          setMedicines((prev) =>
            prev.map((med, i) => i === index ? { ...med, medicine_salt: newName, isNewSalt: false } : med)
          );
          setErrors((prev) => { const copy = { ...prev }; if (copy[index]) delete copy[index].medicine_salt; return copy; });
          showToast(result?.status?.message || "Salt created successfully", "SUCCESS");
        } else {
          showToast(result?.status?.message || "Failed to create salt", "ERROR");
        }
      }
    } catch {
      showToast("Failed to create salt", "ERROR");
    } finally {
      setSaltLoadingIndex(null);
    }
  };

  const handleDeleteRow = (index) => {
    setMedicines((prev) => prev.filter((_, i) => i !== index));
  };

  const validateMedicines = () => {
    const newErrors = {};
    let isValid = true;

    medicines.forEach((med, index) => {
      const rowErrors = {};

      if (!med.medicine_name?.trim()) {
        rowErrors.medicine_name = true;
        isValid = false;
      }

      if (!med.medicine_salt?.trim()) {
        rowErrors.medicine_salt = "Medicine salt is required";
        isValid = false;
      } else if (med.isNewSalt) {
        const saltValidation = validateMedicineSalt(med.medicine_salt);
        if (!saltValidation.valid) {
          rowErrors.medicine_salt = saltValidation.message;
          isValid = false;
        }
      }

      if (!med.form_type?.trim()) {
        rowErrors.form_type = true;
        isValid = false;
      }

      if (Object.keys(rowErrors).length > 0) {
        newErrors[index] = rowErrors;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateMedicines()) return;

    try {
      const payload = {
        medicines: medicines.map((med) => ({
          medicine_name: med.medicine_name.trim(),
          form_type: med.form_type,
          salt: med.medicine_salt.trim(),
        })),
      };

      await postBulkMedicine(payload);
      showToast("Medicines Created Successfully", "SUCCESS");
      onClose();
    } catch (error) {
      console.log(error);
    }
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fileAccept = ".jpg,.jpeg,.pdf,.png";

  return (
    <Box
      sx={{
        height: "90vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
        position="sticky"
        zIndex={10}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Typography
            variant="title_medium"
            gutterBottom
            sx={{ fontSize: "20px" }}
          >
            Upload Medicines Image
          </Typography>
        </Box>
        <IconButton
          sx={{ marginBottom: "r0em" }}
          onClick={() => {
            onClose();
          }}
        >
          <CloseIcon size="small" sx={{ color: "red" }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",

          /* Chrome, Safari */
          "&::-webkit-scrollbar": {
            display: "none",
          },

          /* Firefox */
          scrollbarWidth: "none",

          /* IE / Edge */
          msOverflowStyle: "none",
        }}
      >
        {step === 1 && (
          <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                border: "2px dashed #E1E1E1",
                borderRadius: "16px",
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: "neutral.backGround",
                transition: "0.3s",
                "&:hover": {
                  borderColor: "neutral.teal",
                  backgroundColor: "neutral.blueShadeBg",
                },
              }}
              onClick={() => document.getElementById("medicine-upload").click()}
            >
              <input
                id="medicine-upload"
                type="file"
                hidden
                accept={fileAccept}
                // capture="environment"   // 📱 mobile camera + file
                onChange={(e) => setFile(e.target.files[0])}
              />

              {!file ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, alignItems: "center" }}
                    >
                      Upload medicines Image / PDF
                    </Typography>
                    {/* <FileUploadOutlinedIcon sx={{}} /> */}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Click to upload or take photo
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PNG, JPG, JPEG, PDF up to 10MB
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {file.name}
                  </Typography>

                  {/* <Box
                                        component="img"
                                        src={URL.createObjectURL(file)}
                                        alt="preview"
                                        sx={{
                                            width: "100%",
                                            maxHeight: 220,
                                            objectFit: "contain",
                                            borderRadius: "12px",
                                            mb: 1,
                                        }}
                                    /> */}

                  {/* IMAGE PREVIEW */}
                  {file.type.startsWith("image/") && (
                    <Box
                      component="img"
                      src={URL.createObjectURL(file)}
                      alt="preview"
                      sx={{
                        width: "100%",
                        maxHeight: 220,
                        objectFit: "contain",
                        borderRadius: "12px",
                        mb: 1,
                      }}
                    />
                  )}

                  {/* PDF PREVIEW */}
                  {file.type === "application/pdf" && (
                    <Box
                      sx={{
                        width: "100%",
                        height: "50vh",
                        border: "1px solid #ddd",
                        borderRadius: "12px",
                        overflow: "hidden",
                        mb: 1,
                      }}
                    >
                      <iframe
                        src={URL.createObjectURL(file)}
                        title="PDF Preview"
                        width="100%"
                        height="100%"
                        style={{ border: "none" }}
                      />
                    </Box>
                  )}

                  <Button
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                  >
                    Remove
                  </Button>
                </>
              )}
            </Box>

            <Button
              onClick={handleUpload}
              disabled={loading}
              sx={{ ...primaryBtnTeal, alignSelf: "flex-end", maxWidth: "255" }}
              mt={2}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                "Upload"
              )}
            </Button>
          </Box>
        )}

        {step === 2 && (
          <Box
            sx={{
              borderRadius: "14px",
              ...(!isMobile && {
                border: "1px solid #ccc",
                p: 1,
              }),
            }}
          >
            {/* ===== MOBILE VIEW ===== */}
            {isMobile ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {medicines.map((item, index) => (
                  <Card
                    key={index}
                    sx={{
                      borderRadius: "16px",
                      position: "relative",
                      px: 0,
                      border: "1px solid #ccc",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1,
                      }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: "50%",
                          textAlign: "center",
                        }}
                      >
                        <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                          {" "}
                          {index + 1}
                        </Typography>
                      </Box>
                      {/* Delete */}
                      <IconButton
                        // sx={{ position: "absolute", top: -1, right: 8 }}
                        onClick={() => handleDeleteRow(index)}
                      >
                        <CloseIcon sx={xBtn} />
                      </IconButton>
                    </Box>

                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        px: 1,
                        py: 1,
                        // paddingTop: 6,
                      }}
                    >
                      {/* Medicine Name */}
                      <TextField
                        fullWidth
                        placeholder="Medicine name"
                        value={item.medicine_name}
                        sx={{
                          ...textfieldText,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            height: "45px",
                            padding: "9px !important",
                            "& input": {
                              padding: "4px",
                              fontSize: "14px",
                              fontFamily: "Lato !important",
                            },
                          },
                        }}
                        error={!!errors[index]?.medicine_name}
                        helperText={
                          errors[index]?.medicine_name &&
                          "Medicine name is required"
                        }
                        onChange={(e) => {
                          setMedicines((prev) =>
                            prev.map((med, i) =>
                              i === index
                                ? { ...med, medicine_name: e.target.value }
                                : med,
                            ),
                          );
                        }}
                      />

                      {/* Medicine Salt */}
                      <Box display="flex" alignItems="center" gap="4px">
                        <Box sx={{ flexGrow: 1 }}>
                          <CreatableSelect
                            placeholder="Medicine salt e.g. PARACETAMOL 500 MG"
                            isClearable
                            isLoading={saltLoadingIndex === index || medicineSaltLoading}
                            options={[...medicineSaltOptions, ...extraSaltOptions]}
                            value={
                              item.medicine_salt
                                ? { label: item.medicine_salt, value: item.medicine_salt }
                                : null
                            }
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            formatCreateLabel={(input) => `Create "${normalizeMedicineSalt(input)}"`}
                            formatOptionLabel={(option, { context }) =>
                              context === "menu" ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  {option?.__isNew__ ? (
                                    <AddCircleIcon sx={{ fontSize: 18, color: "#1b7f3b" }} />
                                  ) : (
                                    <ScienceOutlinedIcon sx={{ fontSize: 18, color: "#7a869a" }} />
                                  )}
                                  <span>{option.label}</span>
                                </Box>
                              ) : (
                                option.label
                              )
                            }
                            onCreateOption={(inputValue) => handleCreateSaltAtIndex(inputValue, index)}
                            styles={{
                              ...selectStyles,
                              option: (base, state) => ({
                                ...base,
                                cursor: "pointer",
                                ...(state.data?.__isNew__
                                  ? {
                                      color: "#1b7f3b",
                                      fontWeight: 600,
                                      backgroundColor: state.isFocused ? "#d6f0df" : "#eafaf0",
                                    }
                                  : {
                                      color: "#3a3a3a",
                                      backgroundColor: state.isFocused ? "#f0f7ff" : "#fff",
                                    }),
                              }),
                              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
                            }}
                            onChange={(selected) => {
                              const normalized = normalizeMedicineSalt(selected?.label || "");
                              setMedicines((prev) =>
                                prev.map((med, i) =>
                                  i === index ? { ...med, medicine_salt: normalized, isNewSalt: false } : med,
                                ),
                              );
                            }}
                          />
                        </Box>
                        <Tooltip title="Auto-fill salt from medicine name" placement="top">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleAutoFillSalt(index)}
                              disabled={saltLoadingIndex === index}
                              sx={{
                                color: "#2e7d32",
                                "&:hover": { backgroundColor: "#e8f5e9" },
                              }}
                            >
                              {saltLoadingIndex === index ? (
                                <CircularProgress size={14} sx={{ color: "#2e7d32" }} />
                              ) : (
                                <AutoFixHighIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>

                      {errors[index]?.medicine_salt && (
                        <Typography variant="caption" color="error">
                          {errors[index].medicine_salt}
                        </Typography>
                      )}

                      {/* Form Type */}
                      <Autocomplete
                        options={formType?.payload?.form_types || []}
                        value={
                          formType?.payload?.form_types?.find(
                            (opt) => opt.value === item.form_type,
                          ) || null
                        }
                        sx={{
                          ...autoFocusOffWithOutBorder,
                          backgroundColor: "neutral.white",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            height: "50px",
                            padding: "9px !important",
                            "& input": {
                              padding: "8px",
                              fontSize: "14px",
                            },
                            "& .MuiAutocomplete-popupIndicator": {
                              display: "none",
                            },
                          },
                        }}
                        onChange={(e, value) => {
                          setMedicines((prev) =>
                            prev.map((med, i) =>
                              i === index
                                ? { ...med, form_type: value?.value || "" }
                                : med,
                            ),
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Form type"
                            error={!!errors[index]?.form_type}
                            helperText={
                              errors[index]?.form_type &&
                              "Form type is required"
                            }
                          />
                        )}
                      />
                    </CardContent>
                  </Card>
                ))}

                {/* Add More */}
                <Button
                  onClick={handleAddRow}
                  sx={{
                    backgroundColor: "neutral.teal",
                    padding: "0px",
                    "&:hover": { backgroundColor: "neutral.teal" },
                    alignSelf: "center",
                    width: "100%",
                    height: "35px",
                    borderRadius: "12px",
                    marginY: 1,
                  }}
                >
                  <Typography sx={{ color: "neutral.white" }}>
                    Add Medicine{" "}
                  </Typography>
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  {/* ===== TABLE HEADER ===== */}
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Medicine</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Medicine Salt
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Form Type</TableCell>
                      <TableCell align="right">
                        <IconButton
                          id="addMoreBtn"
                          sx={{ ...orangeButton, marginRight: 1 }}
                          onClick={handleAddRow}
                        >
                          <AddIcon
                            sx={{
                              color: "neutral.white",
                              width: 26,
                              height: 26,
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  {/* ===== TABLE BODY ===== */}
                  <TableBody>
                    {medicines?.map((item, index) => (
                      <TableRow key={index}>
                        {/* Medicine Name */}
                        <TableCell width="33%">
                          <TextField
                            fullWidth
                            error={!!errors[index]?.medicine_name}
                            helperText={
                              errors[index]?.medicine_name
                                ? "Medicine name is required"
                                : ""
                            }
                            placeholder="Medicine name"
                            value={item.medicine_name}
                            onChange={(e) => {
                              setMedicines((prev) =>
                                prev.map((med, i) =>
                                  i === index
                                    ? { ...med, medicine_name: e.target.value }
                                    : med,
                                ),
                              );
                              setErrors((prev) => {
                                const copy = { ...prev };
                                if (copy[index])
                                  delete copy[index].medicine_name;
                                return copy;
                              });
                            }}
                            sx={{
                              ...textfieldText,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                height: "45px",
                                padding: "9px !important",
                                "& input": {
                                  padding: "4px",
                                  fontSize: "14px",
                                  fontFamily: "Lato !important",
                                },
                              },
                            }}
                          />
                        </TableCell>

                        {/* Medicine Salt */}
                        <TableCell width="33%">
                          <Box display="flex" alignItems="center" gap="4px">
                            <Box sx={{ flexGrow: 1 }}>
                              <CreatableSelect
                                placeholder="e.g. PARACETAMOL 500 MG"
                                isClearable
                                isLoading={saltLoadingIndex === index || medicineSaltLoading}
                                options={[...medicineSaltOptions, ...extraSaltOptions]}
                                value={
                                  item?.medicine_salt
                                    ? { label: item.medicine_salt, value: item.medicine_salt }
                                    : null
                                }
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                formatCreateLabel={(input) => `Create "${normalizeMedicineSalt(input)}"`}
                                formatOptionLabel={(option, { context }) =>
                                  context === "menu" ? (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      {option?.__isNew__ ? (
                                        <AddCircleIcon sx={{ fontSize: 18, color: "#1b7f3b" }} />
                                      ) : (
                                        <ScienceOutlinedIcon sx={{ fontSize: 18, color: "#7a869a" }} />
                                      )}
                                      <span>{option.label}</span>
                                    </Box>
                                  ) : (
                                    option.label
                                  )
                                }
                                onCreateOption={(inputValue) => handleCreateSaltAtIndex(inputValue, index)}
                                styles={{
                                  ...selectStyles,
                                  option: (base, state) => ({
                                    ...base,
                                    cursor: "pointer",
                                    ...(state.data?.__isNew__
                                      ? {
                                          color: "#1b7f3b",
                                          fontWeight: 600,
                                          backgroundColor: state.isFocused ? "#d6f0df" : "#eafaf0",
                                        }
                                      : {
                                          color: "#3a3a3a",
                                          backgroundColor: state.isFocused ? "#f0f7ff" : "#fff",
                                        }),
                                  }),
                                  menuPortal: (base) => ({ ...base, zIndex: 999999, fontFamily: "Lato" }),
                                  menu: (provided) => ({ ...provided, zIndex: 999999 }),
                                }}
                                onChange={(selected) => {
                                  const normalized = normalizeMedicineSalt(selected?.label || "");
                                  setMedicines((prev) =>
                                    prev.map((med, i) =>
                                      i === index ? { ...med, medicine_salt: normalized, isNewSalt: false } : med,
                                    ),
                                  );
                                  setErrors((prev) => {
                                    const copy = { ...prev };
                                    if (copy[index]) delete copy[index].medicine_salt;
                                    return copy;
                                  });
                                }}
                              />
                            </Box>
                            <Tooltip title="Auto-fill salt from medicine name" placement="top">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleAutoFillSalt(index)}
                                  disabled={saltLoadingIndex === index}
                                  sx={{
                                    color: "#2e7d32",
                                    "&:hover": { backgroundColor: "#e8f5e9" },
                                  }}
                                >
                                  {saltLoadingIndex === index ? (
                                    <CircularProgress size={14} sx={{ color: "#2e7d32" }} />
                                  ) : (
                                    <AutoFixHighIcon sx={{ fontSize: 18 }} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>

                          {errors[index]?.medicine_salt && (
                            <Typography variant="caption" color="error">
                              {errors[index].medicine_salt}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Form Type */}
                        <TableCell width="31%">
                          <Autocomplete
                            options={
                              formType?.payload?.form_types?.filter(Boolean) ||
                              []
                            }
                            getOptionLabel={(option) => option?.label || ""}
                            value={
                              formType?.payload?.form_types?.find(
                                (opt) => opt.value === item.form_type,
                              ) || null
                            }
                            sx={{
                              ...autoFocusOffWithOutBorder,
                              backgroundColor: "neutral.white",
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                height: "50px",
                                padding: "9px !important",
                                "& input": {
                                  padding: "8px",
                                  fontSize: "14px",
                                },
                                "& .MuiAutocomplete-popupIndicator": {
                                  display: "none",
                                },
                              },
                            }}
                            onChange={(e, value) => {
                              setMedicines((prev) =>
                                prev.map((med, i) =>
                                  i === index
                                    ? { ...med, form_type: value?.value || "" }
                                    : med,
                                ),
                              );

                              // ✅ clear error
                              setErrors((prev) => {
                                const copy = { ...prev };
                                if (copy[index]) delete copy[index].form_type;
                                return copy;
                              });
                            }}
                            renderInput={(params) => (
                              <TextField
                                error={!!errors[index]?.form_type}
                                helperText={
                                  errors[index]?.form_type
                                    ? "Form type is required"
                                    : ""
                                }
                                {...params}
                                placeholder="Form type"
                              />
                            )}
                          />
                        </TableCell>

                        {/* Delete */}
                        <TableCell align="right">
                          <IconButton onClick={() => handleDeleteRow(index)}>
                            <CloseIcon sx={xBtn} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Box>

      {step === 2 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
          }}
        >
          <Button
            sx={{
              ...primaryBtnTeal,
            }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {/* <Typography variant="heading2_14" color="neutral.white"> */}
            Submit
            {/* </Typography> */}
          </Button>
        </Box>
      )}
    </Box>
  );
};
export default MedicineImageUpload;
